import axios from "axios";
export default {
  namespaced: true,
  state: {
    // User's tasks
    my_tasks_today: [],
    pending_tasks: [],
    my_upcoming_tasks: [],
    my_completed_tasks: [],
    my_recurring_tasks: [],

    // Admin data
    all_tasks: [],
    recurring_tasks: [],
    task_logs: [],
    dashboard_stats: null,
    calendar_data: [],
    user_progress: null,

    // Daily report & user history (separate from task_logs to avoid collision)
    daily_report: null,
    user_history_logs: [],

    // UI state (scheduled popup - 8AM, 12PM, 3PM)
    popup_tasks: [],
    show_popup: false,
    last_popup_time: null,

    // Notification state (2-minute polling)
    task_notifications: [],
    show_notification_dialog: false,
    last_notification_check: null,
    // Keys of dismissed notifications to suppress re-display.
    // Each key is "taskId_updatedAt" so re-edits produce a new key.
    dismissed_notification_keys: [],
  },
  getters: {
    my_tasks_today(state) {
      return state.my_tasks_today;
    },
    pending_tasks(state) {
      return state.pending_tasks;
    },
    pending_count(state) {
      return state.pending_tasks.length;
    },
    my_upcoming_tasks(state) {
      return state.my_upcoming_tasks;
    },
    my_completed_tasks(state) {
      return state.my_completed_tasks;
    },
    my_recurring_tasks(state) {
      return state.my_recurring_tasks;
    },
    overdue_tasks(state) {
      const today = new Date().toISOString().substr(0, 10);
      return state.pending_tasks.filter((t) => t.log_date < today);
    },
    all_tasks(state) {
      return state.all_tasks;
    },
    recurring_tasks(state) {
      return state.recurring_tasks;
    },
    task_logs(state) {
      return state.task_logs;
    },
    dashboard_stats(state) {
      return state.dashboard_stats;
    },
    calendar_data(state) {
      return state.calendar_data;
    },
    user_progress(state) {
      return state.user_progress;
    },
    daily_report(state) {
      return state.daily_report;
    },
    user_history_logs(state) {
      return state.user_history_logs;
    },
    popup_tasks(state) {
      return state.popup_tasks;
    },
    show_popup(state) {
      return state.show_popup;
    },
    task_notifications(state) {
      return state.task_notifications;
    },
    show_notification_dialog(state) {
      return state.show_notification_dialog;
    },
    notification_count(state) {
      return state.task_notifications.length;
    },
    last_notification_check(state) {
      return state.last_notification_check;
    },
  },
  mutations: {
    SET_MY_TASKS_TODAY(state, tasks) {
      state.my_tasks_today = tasks;
    },
    SET_PENDING_TASKS(state, tasks) {
      state.pending_tasks = tasks;
    },
    SET_MY_UPCOMING_TASKS(state, tasks) {
      state.my_upcoming_tasks = tasks;
    },
    SET_MY_COMPLETED_TASKS(state, logs) {
      state.my_completed_tasks = logs;
    },
    SET_MY_RECURRING_TASKS(state, tasks) {
      state.my_recurring_tasks = tasks;
    },
    SET_ALL_TASKS(state, tasks) {
      state.all_tasks = tasks;
    },
    SET_RECURRING_TASKS(state, tasks) {
      state.recurring_tasks = tasks;
    },
    ADD_TASK(state, task) {
      // task can be a single task or array of tasks (multi-user assignment)
      if (Array.isArray(task)) {
        task.forEach((t) => state.all_tasks.unshift(t));
      } else {
        state.all_tasks.unshift(task);
      }
    },
    EDIT_TASK(state, task) {
      var index = state.all_tasks.findIndex((a) => a.id == task.id);
      if (index !== -1) {
        state.all_tasks.splice(index, 1, task);
      }
      // Also update in recurring_tasks so card view reflects changes
      var rIndex = state.recurring_tasks.findIndex((a) => a.id == task.id);
      if (rIndex !== -1) {
        if (
          task.recurrence_type &&
          task.recurrence_type !== "none" &&
          task.status === "active"
        ) {
          state.recurring_tasks.splice(rIndex, 1, task);
        } else {
          // No longer recurring or no longer active - remove from list
          state.recurring_tasks.splice(rIndex, 1);
        }
      }
    },
    DELETE_TASK(state, taskId) {
      state.all_tasks = state.all_tasks.filter((t) => t.id != taskId);
      state.recurring_tasks = state.recurring_tasks.filter(
        (t) => t.id != taskId
      );
    },
    SET_TASK_LOGS(state, logs) {
      state.task_logs = logs;
    },
    UPDATE_TASK_LOG(state, log) {
      // Update in task_logs list
      var index = state.task_logs.findIndex((a) => a.id == log.id);
      if (index !== -1) {
        state.task_logs.splice(index, 1, log);
      }
      // Update in my_tasks_today
      var index2 = state.my_tasks_today.findIndex((a) => a.id == log.id);
      if (index2 !== -1) {
        state.my_tasks_today.splice(index2, 1, log);
      }
      // Update in pending_tasks (ongoing stays, completed/acknowledged removed)
      if (log.status === "completed" || log.status === "acknowledged") {
        // Also remove any other logs for the same recurring task (they were bulk-completed on backend)
        var taskId = log.task_id || (log.task && log.task.id);
        state.pending_tasks = state.pending_tasks.filter(
          (t) =>
            t.id != log.id && !(taskId && t.task_id == taskId && t.id != log.id)
        );
        state.popup_tasks = state.popup_tasks.filter(
          (t) =>
            t.id != log.id && !(taskId && t.task_id == taskId && t.id != log.id)
        );
      } else {
        // Update the log in pending_tasks if it exists (e.g. status changed to ongoing)
        var index3 = state.pending_tasks.findIndex((a) => a.id == log.id);
        if (index3 !== -1) {
          state.pending_tasks.splice(index3, 1, log);
        }
      }
      // Update in user_history_logs
      var index4 = state.user_history_logs.findIndex((a) => a.id == log.id);
      if (index4 !== -1) {
        state.user_history_logs.splice(index4, 1, log);
      }
    },
    SET_DASHBOARD_STATS(state, stats) {
      state.dashboard_stats = stats;
    },
    SET_CALENDAR_DATA(state, data) {
      state.calendar_data = data;
    },
    SET_USER_PROGRESS(state, data) {
      state.user_progress = data;
    },
    SET_DAILY_REPORT(state, data) {
      state.daily_report = data;
    },
    SET_USER_HISTORY_LOGS(state, logs) {
      state.user_history_logs = logs;
    },
    SET_POPUP_TASKS(state, tasks) {
      state.popup_tasks = tasks;
    },
    SET_SHOW_POPUP(state, val) {
      state.show_popup = val;
    },
    SET_LAST_POPUP_TIME(state, time) {
      state.last_popup_time = time;
    },

    // ---- Notification mutations ----
    SET_TASK_NOTIFICATIONS(state, tasks) {
      state.task_notifications = tasks;
    },
    SET_SHOW_NOTIFICATION_DIALOG(state, val) {
      state.show_notification_dialog = val;
    },
    SET_LAST_NOTIFICATION_CHECK(state, timestamp) {
      state.last_notification_check = timestamp;
    },
    ADD_DISMISSED_NOTIFICATION_KEYS(state, keys) {
      // Merge new keys into the dismissed list, avoid duplicates
      keys.forEach((key) => {
        if (state.dismissed_notification_keys.indexOf(key) === -1) {
          state.dismissed_notification_keys.push(key);
        }
      });
      // Cap at 500 entries to prevent unbounded growth over long sessions
      if (state.dismissed_notification_keys.length > 500) {
        state.dismissed_notification_keys =
          state.dismissed_notification_keys.slice(-500);
      }
    },
    CLEAR_NOTIFICATION_STATE(state) {
      state.task_notifications = [];
      state.show_notification_dialog = false;
      state.last_notification_check = null;
      state.dismissed_notification_keys = [];
    },
  },
  actions: {
    // ---- User task actions ----
    async get_my_tasks_today({ commit }, date) {
      let response = await axios.get("/tasks/my/" + date);
      commit("SET_MY_TASKS_TODAY", response.data);
    },
    async get_pending_tasks({ commit }) {
      let response = await axios.get("/tasks/pending");
      commit("SET_PENDING_TASKS", response.data);
    },
    async complete_task_log({ commit }, { logId, completion_note }) {
      let response = await axios.put("/task_log/" + logId + "/complete", {
        completion_note,
      });
      commit("UPDATE_TASK_LOG", response.data);
    },
    async update_task_log_status({ commit }, { logId, status, remark }) {
      let response = await axios.put("/task_log/" + logId + "/update-status", {
        status,
        remark,
      });
      commit("UPDATE_TASK_LOG", response.data);
      return response.data;
    },
    async get_task_log_remarks(_, logId) {
      let response = await axios.get("/task_log/" + logId + "/remarks");
      return response.data;
    },

    // ---- User upcoming / completed / recurring ----
    async get_my_upcoming_tasks({ commit }, days) {
      let response = await axios.get("/tasks/my-upcoming", {
        params: { days: days || 7 },
      });
      commit("SET_MY_UPCOMING_TASKS", response.data);
    },
    async get_my_completed_tasks({ commit }, filters) {
      let params = filters || {};
      let response = await axios.get("/task_logs", { params });
      let completed = (response.data || []).filter(
        (l) => l.status === "completed" || l.status === "acknowledged"
      );
      commit("SET_MY_COMPLETED_TASKS", completed);
    },
    async get_my_recurring_tasks({ commit, rootGetters }) {
      const userId = rootGetters["auth/user"].id;
      let response = await axios.get("/tasks", {
        params: { assigned_to: userId, status: "active" },
      });
      let recurring = (response.data || []).filter(
        (t) => t.recurrence_type && t.recurrence_type !== "none"
      );
      commit("SET_MY_RECURRING_TASKS", recurring);
    },

    // ---- Admin task CRUD ----
    async get_all_tasks({ commit }, filters) {
      let params = filters || {};
      let response = await axios.get("/tasks", { params });
      commit("SET_ALL_TASKS", response.data);
    },
    async get_recurring_tasks({ commit }) {
      let response = await axios.get("/tasks", {
        params: { status: "active" },
      });
      let recurring = (response.data || []).filter(
        (t) => t.recurrence_type && t.recurrence_type !== "none"
      );
      commit("SET_RECURRING_TASKS", recurring);
    },
    async create_task({ commit }, payload) {
      let response = await axios.post("/task", payload);
      commit("ADD_TASK", response.data);
      return response.data;
    },
    async update_task({ commit }, payload) {
      let response = await axios.put("/task", payload);
      commit("EDIT_TASK", response.data);
      return response.data;
    },
    async delete_task({ commit }, taskId) {
      await axios.delete("/task/" + taskId);
      commit("DELETE_TASK", taskId);
    },

    // ---- Admin acknowledge ----
    async acknowledge_task_log({ commit }, { logId, admin_comment }) {
      let response = await axios.put("/task_log/" + logId + "/acknowledge", {
        admin_comment,
      });
      commit("UPDATE_TASK_LOG", response.data);
    },
    async bulk_acknowledge(_, { logIds, admin_comment }) {
      let response = await axios.put("/task_logs/acknowledge", {
        log_ids: logIds,
        admin_comment,
      });
      return response.data;
    },

    // ---- Admin dashboard ----
    async get_task_logs({ commit }, filters) {
      let params = filters || {};
      let response = await axios.get("/task_logs", { params });
      commit("SET_TASK_LOGS", response.data);
    },
    async get_dashboard_stats({ commit }, filters) {
      let params = filters || {};
      let response = await axios.get("/tasks/dashboard", { params });
      commit("SET_DASHBOARD_STATS", response.data);
    },
    async get_user_progress({ commit }, userId) {
      let response = await axios.get("/tasks/dashboard/user/" + userId);
      commit("SET_USER_PROGRESS", response.data);
    },
    async get_calendar_data({ commit }, { month, user_id }) {
      let params = { month };
      if (user_id) params.user_id = user_id;
      let response = await axios.get("/tasks/dashboard/calendar", { params });
      commit("SET_CALENDAR_DATA", response.data);
    },

    // ---- Daily report & user history ----
    async get_daily_report({ commit }, filters) {
      let params = filters || {};
      let response = await axios.get("/task_logs/daily-report", { params });
      commit("SET_DAILY_REPORT", response.data);
    },
    async get_user_history_logs({ commit }, filters) {
      let params = filters || {};
      let response = await axios.get("/task_logs", { params });
      commit("SET_USER_HISTORY_LOGS", response.data);
    },

    // ---- Popup actions (scheduled reminders) ----
    trigger_popup({ commit, state }) {
      commit("SET_POPUP_TASKS", [...state.pending_tasks]);
      commit("SET_SHOW_POPUP", true);
    },
    dismiss_popup({ commit }) {
      commit("SET_SHOW_POPUP", false);
      commit("SET_LAST_POPUP_TIME", Date.now());
    },

    // ---- Notification actions (2-minute polling) ----

    /**
     * Fetch task notifications from the server since last check.
     * Filters out already-dismissed notifications and triggers the dialog
     * only if there are genuinely new/unseen notifications.
     */
    async fetch_task_notifications({ commit, state, dispatch }) {
      const since = state.last_notification_check || new Date().toISOString();

      // Fetch assignment notifications and acknowledged notifications in parallel
      const [assignRes, ackRes] = await Promise.all([
        axios.get("/tasks/notifications", { params: { since } }),
        axios.get("/task_logs/acknowledged-notifications", {
          params: { since },
        }),
      ]);

      const { tasks, checked_at } = assignRes.data;
      const { logs: ackLogs, checked_at: ackCheckedAt } = ackRes.data;

      const dismissed = state.dismissed_notification_keys;

      // Filter assignment notifications
      const newTasks = (tasks || []).filter((t) => {
        var key = t._notification_key || t.id + "_" + t.updated_at;
        return dismissed.indexOf(key) === -1;
      });

      // Map acknowledged logs to the same notification shape
      // Note: Laravel serializes the acknowledgedBy relationship under
      // the "acknowledged_by" key (overwriting the FK integer when loaded)
      const ackNotifications = (ackLogs || [])
        .map((log) => ({
          id: log.id,
          title: log.task ? log.task.title : "Task",
          category: log.task ? log.task.category : null,
          priority: log.task ? log.task.priority : "basic",
          notification_type: "acknowledged",
          acknowledged_by_name:
            log.acknowledged_by && typeof log.acknowledged_by === "object"
              ? log.acknowledged_by.full_name
              : "Admin",
          admin_comment: log.admin_comment,
          completion_note: log.completion_note,
          remarks: log.remarks || [],
          acknowledged_at: log.acknowledged_at,
          log_date: log.log_date,
          _notification_key: "ack_" + log.id + "_" + log.acknowledged_at,
        }))
        .filter((n) => dismissed.indexOf(n._notification_key) === -1);

      // Use the later of the two checked_at timestamps
      commit(
        "SET_LAST_NOTIFICATION_CHECK",
        ackCheckedAt > checked_at ? ackCheckedAt : checked_at
      );

      const allNotifications = newTasks.concat(ackNotifications);
      if (allNotifications.length > 0) {
        commit("SET_TASK_NOTIFICATIONS", allNotifications);
        commit("SET_SHOW_NOTIFICATION_DIALOG", true);

        // Refresh user's task lists so "My Tasks" panel reflects the new data
        const today = new Date().toISOString().substr(0, 10);
        dispatch("get_my_tasks_today", today).catch(() => {});
        dispatch("get_pending_tasks").catch(() => {});
      }

      return allNotifications;
    },

    /**
     * Dismiss the notification dialog and mark all currently displayed
     * notifications as dismissed so they will not re-appear.
     */
    dismiss_task_notifications({ commit, state }) {
      // Build keys for all currently shown notifications
      var keys = state.task_notifications.map(
        (t) => t._notification_key || t.id + "_" + t.updated_at
      );
      commit("ADD_DISMISSED_NOTIFICATION_KEYS", keys);
      commit("SET_TASK_NOTIFICATIONS", []);
      commit("SET_SHOW_NOTIFICATION_DIALOG", false);
    },

    /**
     * Dismiss a single acknowledged task notification permanently.
     * Calls the backend so it won't reappear on future logins.
     */
    async dismiss_single_acknowledged({ commit, state }, logId) {
      // Grab key before removing from list
      var target = state.task_notifications.find((t) => t.id === logId);
      if (target) {
        commit("ADD_DISMISSED_NOTIFICATION_KEYS", [
          target._notification_key || target.id + "_" + target.updated_at,
        ]);
      }

      await axios.put("/task_log/" + logId + "/dismiss");

      // Remove from local notifications list
      var remaining = state.task_notifications.filter((t) => t.id !== logId);
      commit("SET_TASK_NOTIFICATIONS", remaining);
    },

    /**
     * One-time fetch on login to catch acknowledgments that happened
     * while the user was logged out. Uses a 7-day lookback window.
     */
    async fetch_acknowledged_on_login({ commit, state }) {
      const since = new Date(
        Date.now() - 7 * 24 * 60 * 60 * 1000
      ).toISOString();
      let response = await axios.get("/task_logs/acknowledged-notifications", {
        params: { since },
      });
      const { logs: ackLogs } = response.data;
      const dismissed = state.dismissed_notification_keys;

      const ackNotifications = (ackLogs || [])
        .map((log) => ({
          id: log.id,
          title: log.task ? log.task.title : "Task",
          category: log.task ? log.task.category : null,
          priority: log.task ? log.task.priority : "basic",
          notification_type: "acknowledged",
          acknowledged_by_name:
            log.acknowledged_by && typeof log.acknowledged_by === "object"
              ? log.acknowledged_by.full_name
              : "Admin",
          admin_comment: log.admin_comment,
          completion_note: log.completion_note,
          remarks: log.remarks || [],
          acknowledged_at: log.acknowledged_at,
          log_date: log.log_date,
          _notification_key: "ack_" + log.id + "_" + log.acknowledged_at,
        }))
        .filter((n) => dismissed.indexOf(n._notification_key) === -1);

      if (ackNotifications.length > 0) {
        // Merge with any existing notifications
        const existing = state.task_notifications;
        commit("SET_TASK_NOTIFICATIONS", existing.concat(ackNotifications));
        commit("SET_SHOW_NOTIFICATION_DIALOG", true);
      }

      return ackNotifications;
    },

    /**
     * Clear all notification state. Called on logout.
     */
    clear_notification_state({ commit }) {
      commit("CLEAR_NOTIFICATION_STATE");
    },
  },
};
